import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Button,
  Slider,
  Divider,
  Grid,
  Tooltip,
  Fade,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Collapse,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Undo as UndoIcon,
  RestaurantMenu as SpiceIcon,
  AccessTime as TimeIcon,
  Water as WaterIcon,
  Opacity as OilIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";

const RecipeCustomizer = ({
  tasks,
  onTasksUpdate,
  onClose,
  recipe,
  originalQuantities = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State for tracking changes
  const [modifications, setModifications] = useState({});
  const [spicyLevel, setSpicyLevel] = useState(3); // 1-5 scale
  const [expandedSections, setExpandedSections] = useState({
    spicy: true,
    time: true,
    ingredients: true,
  });

  // Store original quantities on first render
  const [storedOriginalQuantities] = useState(() => {
    const stored = {};
    tasks.forEach((task) => {
      if (task.item_name && task.quantity) {
        // Use provided original quantity or current quantity as original
        stored[task.id] =
          originalQuantities[task.id] || parseFloat(task.quantity);
      }
      if (
        (task.category === "Waiting Action" ||
          task.type === "choose_seconds") &&
        task.quantity
      ) {
        stored[task.id] =
          originalQuantities[task.id] || parseInt(task.quantity);
      }
    });
    return stored;
  });

  // Extract modifiable elements from tasks
  const modifiableElements = useMemo(() => {
    const ingredients = tasks.filter(
      (task) =>
        task.item_name &&
        task.quantity &&
        [
          "choose_spices",
          "choose_oil",
          "choose_water",
          "choose_cup",
          "choose_grains",
          "choose_paste",
        ].includes(task.type)
    );

    // Updated cooking times to target Waiting Action category and choose_seconds type
    const cookingTimes = tasks.filter(
      (task) =>
        (task.category === "Waiting Action" ||
          task.type === "choose_seconds") &&
        task.quantity
    );

    // Updated spice detection - check task.args for spicy words with safety check
    const spices = ingredients.filter((task) => {
      // Check task.args for spicy words with safety check
      if (task.args && typeof task.args === "string") {
        const argsLower = task.args.toLowerCase();
        const spicyWords = [
          "mirch",
          "chilly",
          "chilli",
          "chili",
          "pepper",
          "hot",
        ];
        return spicyWords.some((word) => argsLower.includes(word));
      }

      // Check item_name as fallback
      if (task.item_name) {
        const nameLower = task.item_name.toLowerCase();
        const spicyWords = [
          "mirch",
          "chilly",
          "chilli",
          "chili",
          "pepper",
          "hot",
        ];
        return spicyWords.some((word) => nameLower.includes(word));
      }

      return false;
    });

    // Only include water tasks if they exist
    const water = tasks.filter(
      (task) =>
        task.type === "choose_water" ||
        (task.item_name && task.item_name.toLowerCase().includes("water"))
    );

    // Only include oil tasks if they exist
    const oil = tasks.filter(
      (task) =>
        task.type === "choose_oil" ||
        (task.item_name && task.item_name.toLowerCase().includes("oil"))
    );

    return {
      ingredients,
      cookingTimes,
      spices,
      water,
      oil,
      otherIngredients: ingredients.filter(
        (task) =>
          !spices.includes(task) && !water.includes(task) && !oil.includes(task)
      ),
    };
  }, [tasks]);

  // Helper function to get display name for cooking time
  const getCookingTimeDisplay = (seconds) => {
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else if (seconds >= 60) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    }
    return `${seconds}s`;
  };

  // Function to modify ingredient quantity
  const modifyIngredient = useCallback(
    (taskId, changePercent) => {
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      setModifications((prev) => {
        const currentMod = prev[taskId];
        const originalQuantity =
          currentMod?.originalQuantity || storedOriginalQuantities[taskId];

        // Calculate new total change percent from original
        const newTotalChangePercent =
          (currentMod?.changePercent || 0) + changePercent;

        // Always calculate from original quantity
        const newQuantity =
          originalQuantity * (1 + newTotalChangePercent / 100);
        const finalQuantity = Math.max(0, newQuantity);

        return {
          ...prev,
          [taskId]: {
            originalQuantity,
            newQuantity: parseFloat(finalQuantity.toFixed(2)),
            changePercent: newTotalChangePercent,
            type: "ingredient",
            itemName: task.item_name,
            unit: task.unit,
          },
        };
      });
    },
    [tasks, storedOriginalQuantities]
  );

  // Function to modify cooking time
  const modifyCookingTime = useCallback(
    (taskId, changePercent) => {
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;

      setModifications((prev) => {
        const currentMod = prev[taskId];
        const originalTime =
          currentMod?.originalQuantity || storedOriginalQuantities[taskId];

        // Calculate new total change percent from original
        const newTotalChangePercent =
          (currentMod?.changePercent || 0) + changePercent;

        // Always calculate from original time
        const newTime = originalTime * (1 + newTotalChangePercent / 100);
        const finalTime = Math.max(10, Math.round(newTime)); // Minimum 10 seconds

        return {
          ...prev,
          [taskId]: {
            originalQuantity: originalTime,
            newQuantity: finalTime,
            changePercent: newTotalChangePercent,
            type: "time",
            itemName: task.args || `${getCookingTimeDisplay(originalTime)}`,
          },
        };
      });
    },
    [tasks, storedOriginalQuantities]
  );

  // Function to apply spicy level changes
  const applySpicyLevel = useCallback(
    (level) => {
      const spiceMultiplier = {
        1: 0.5, // Mild - 50%
        2: 0.75, // Less spicy - 75%
        3: 1, // Normal - 100%
        4: 1.25, // Spicy - 125%
        5: 1.5, // Very spicy - 150%
      };

      modifiableElements.spices.forEach((spiceTask) => {
        setModifications((prev) => {
          const currentMod = prev[spiceTask.id];
          const originalQuantity =
            currentMod?.originalQuantity ||
            storedOriginalQuantities[spiceTask.id];

          // Calculate new quantity based on spicy level from original
          const newQuantity = originalQuantity * spiceMultiplier[level];
          const changePercent =
            ((newQuantity - originalQuantity) / originalQuantity) * 100;

          return {
            ...prev,
            [spiceTask.id]: {
              originalQuantity,
              newQuantity: parseFloat(newQuantity.toFixed(2)),
              changePercent,
              type: "spicy",
              itemName: spiceTask.item_name,
              unit: spiceTask.unit,
            },
          };
        });
      });
    },
    [modifiableElements.spices, storedOriginalQuantities]
  );

  // Function to undo a modification
  const undoModification = useCallback((taskId) => {
    setModifications((prev) => {
      const newMods = { ...prev };
      delete newMods[taskId];
      return newMods;
    });
  }, []);

  // Function to apply all changes
  const applyChanges = useCallback(() => {
    const updatedTasks = tasks.map((task) => {
      const modification = modifications[task.id];
      if (modification) {
        return {
          ...task,
          quantity: modification.newQuantity.toString(),
          // Update args for cooking time tasks
          ...(modification.type === "time" && {
            args: getCookingTimeDisplay(modification.newQuantity),
            duration: modification.newQuantity,
          }),
        };
      }
      return task;
    });

    // Pass both updated tasks and original quantities to parent
    onTasksUpdate(updatedTasks, storedOriginalQuantities);
    onClose();
  }, [tasks, modifications, onTasksUpdate, onClose, storedOriginalQuantities]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Count total modifications
  const totalModifications = Object.keys(modifications).length;

  const spicyLabels = {
    1: "Mild",
    2: "Less Spicy",
    3: "Normal",
    4: "Spicy",
    5: "Very Spicy",
  };

  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          p: 3,
          textAlign: "center",
        }}
      >
        <TuneIcon sx={{ fontSize: 32, mb: 1 }} />
        <Typography variant="h5" fontWeight="600">
          Customize Your Recipe
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
          {recipe?.recipe_name || "Recipe"}
        </Typography>
        {totalModifications > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${totalModifications} changes made`}
            size="small"
            sx={{
              mt: 1,
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <Stack spacing={2}>
          {/* Spicy Level Control */}
          {modifiableElements.spices.length > 0 && (
            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleSection("spicy")}
                >
                  <SpiceIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    Spicy Level ({modifiableElements.spices.length} ingredients)
                  </Typography>
                  {expandedSections.spicy ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>

                <Collapse in={expandedSections.spicy}>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Adjust spice intensity for detected spicy ingredients
                    </Typography>
                    <Box sx={{ px: 2, mt: 2 }}>
                      <Slider
                        value={spicyLevel}
                        onChange={(_, value) => {
                          setSpicyLevel(value);
                          applySpicyLevel(value);
                        }}
                        min={1}
                        max={5}
                        step={1}
                        marks={Object.entries(spicyLabels).map(
                          ([value, label]) => ({
                            value: parseInt(value),
                            label: isMobile ? value : label,
                          })
                        )}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => spicyLabels[value]}
                        sx={{
                          color: "error.main",
                          "& .MuiSlider-markLabel": {
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        mt: 1,
                        fontWeight: 600,
                        color: theme.palette.error.main,
                      }}
                    >
                      {spicyLabels[spicyLevel]}
                    </Typography>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* Quick Adjustments */}
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center" }}
              >
                <TuneIcon sx={{ mr: 1 }} />
                Quick Adjustments
              </Typography>

              <Grid container spacing={2}>
                {/* Water Controls */}
                {modifiableElements.water.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <WaterIcon color="info" sx={{ mb: 1 }} />
                      <Typography variant="body2" fontWeight="600" gutterBottom>
                        Water
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            modifiableElements.water.forEach((task) =>
                              modifyIngredient(task.id, -10)
                            )
                          }
                          sx={{ bgcolor: "grey.100" }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            modifiableElements.water.forEach((task) =>
                              modifyIngredient(task.id, 10)
                            )
                          }
                          sx={{ bgcolor: "info.light", color: "white" }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Grid>
                )}

                {/* Oil Controls */}
                {modifiableElements.oil.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      <OilIcon color="warning" sx={{ mb: 1 }} />
                      <Typography variant="body2" fontWeight="600" gutterBottom>
                        Oil
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            modifiableElements.oil.forEach((task) =>
                              modifyIngredient(task.id, -10)
                            )
                          }
                          sx={{ bgcolor: "grey.100" }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() =>
                            modifiableElements.oil.forEach((task) =>
                              modifyIngredient(task.id, 10)
                            )
                          }
                          sx={{ bgcolor: "warning.light", color: "white" }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Grid>
                )}

                {/* Cooking Time Controls */}
                {modifiableElements.cookingTimes.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <TimeIcon color="secondary" sx={{ mb: 1 }} />
                      <Typography variant="body2" fontWeight="600" gutterBottom>
                        Cooking Times ({modifiableElements.cookingTimes.length})
                      </Typography>
                      <Stack spacing={2}>
                        {modifiableElements.cookingTimes.map((task) => {
                          const modification = modifications[task.id];
                          const originalTime =
                            storedOriginalQuantities[task.id] ||
                            parseInt(task.quantity);
                          const currentTime =
                            modification?.newQuantity ||
                            parseInt(task.quantity);
                          const changePercent =
                            modification?.changePercent || 0;

                          return (
                            <Paper
                              key={task.id}
                              variant="outlined"
                              sx={{
                                p: 2,
                                bgcolor: modification
                                  ? "action.hover"
                                  : "background.paper",
                                border: modification
                                  ? `1px solid ${theme.palette.secondary.light}`
                                  : undefined,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight="600"
                                  sx={{ flex: 1 }}
                                >
                                  {task.name} -{" "}
                                  {getCookingTimeDisplay(currentTime)}
                                </Typography>
                                {modification && (
                                  <Tooltip title="Undo changes">
                                    <IconButton
                                      size="small"
                                      onClick={() => undoModification(task.id)}
                                      sx={{ color: "secondary.main" }}
                                    >
                                      <UndoIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="center"
                                alignItems="center"
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<RemoveIcon />}
                                  onClick={() =>
                                    modifyCookingTime(task.id, -10)
                                  }
                                  sx={{ minWidth: 100 }}
                                >
                                  -10%
                                </Button>
                                <Box
                                  sx={{ textAlign: "center", minWidth: 120 }}
                                >
                                  {modification && (
                                    <Typography
                                      variant="caption"
                                      color={
                                        changePercent > 0
                                          ? "success.main"
                                          : "error.main"
                                      }
                                    >
                                      {changePercent > 0 ? "+" : ""}
                                      {changePercent.toFixed(0)}%
                                      {changePercent !== 0 &&
                                        ` (was ${getCookingTimeDisplay(
                                          originalTime
                                        )})`}
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => modifyCookingTime(task.id, 10)}
                                  sx={{
                                    bgcolor: "secondary.main",
                                    minWidth: 100,
                                  }}
                                >
                                  +10%
                                </Button>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Individual Ingredients */}
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => toggleSection("ingredients")}
              >
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Individual Ingredients
                </Typography>
                {expandedSections.ingredients ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>

              <Collapse in={expandedSections.ingredients}>
                <Box sx={{ mt: 2 }}>
                  <Stack spacing={2}>
                    {modifiableElements.ingredients.map((task) => {
                      const modification = modifications[task.id];
                      const originalQuantity =
                        storedOriginalQuantities[task.id] ||
                        parseFloat(task.quantity);
                      const currentQuantity =
                        modification?.newQuantity || parseFloat(task.quantity);
                      const changePercent = modification?.changePercent || 0;

                      return (
                        <Fade key={task.id} in timeout={300}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: modification
                                ? "action.hover"
                                : "background.paper",
                              border: modification
                                ? `1px solid ${theme.palette.primary.light}`
                                : undefined,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight="600"
                                sx={{ flex: 1 }}
                              >
                                {task.item_name}
                              </Typography>
                              {modification && (
                                <Tooltip title="Undo changes">
                                  <IconButton
                                    size="small"
                                    onClick={() => undoModification(task.id)}
                                    sx={{ color: "primary.main" }}
                                  >
                                    <UndoIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => modifyIngredient(task.id, -10)}
                                sx={{ bgcolor: "grey.100" }}
                              >
                                <RemoveIcon />
                              </IconButton>

                              <Box sx={{ textAlign: "center", minWidth: 120 }}>
                                <Typography variant="h6" fontWeight="600">
                                  {currentQuantity}
                                  {task.unit}
                                </Typography>
                                {modification && (
                                  <Typography
                                    variant="caption"
                                    color={
                                      changePercent > 0
                                        ? "success.main"
                                        : "error.main"
                                    }
                                  >
                                    {changePercent > 0 ? "+" : ""}
                                    {changePercent.toFixed(0)}%
                                    {changePercent !== 0 &&
                                      ` (was ${originalQuantity}${task.unit})`}
                                  </Typography>
                                )}
                              </Box>

                              <IconButton
                                size="small"
                                onClick={() => modifyIngredient(task.id, 10)}
                                sx={{
                                  bgcolor: "success.light",
                                  color: "white",
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Fade>
                      );
                    })}
                  </Stack>
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          {/* Changes Summary */}
          {totalModifications > 0 && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              action={
                <Button
                  size="small"
                  onClick={() => setModifications({})}
                  color="inherit"
                >
                  Clear All
                </Button>
              }
            >
              You have made {totalModifications} modification
              {totalModifications !== 1 ? "s" : ""} to your recipe.
            </Alert>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            sx={{ minHeight: 48 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={applyChanges}
            disabled={totalModifications === 0}
            fullWidth
            sx={{ minHeight: 48 }}
          >
            Apply Changes ({totalModifications})
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default RecipeCustomizer;
